import type { Character } from '../domain/character.ts';
import { CHARACTER_SCHEMA_VERSION } from '../domain/character.ts';
import { migrateCharacterRecord } from '../domain/migrateCharacter.ts';
import { validateCharacter } from '../domain/validateCharacter.ts';
import type { SessionState } from '../domain/session.ts';
import {
  SESSION_SCHEMA_VERSION,
  migrateSessionRecord,
  validateSession,
} from '../domain/session.ts';

/**
 * Whether a persisted Character or Session record is one THIS BUILD may open.
 *
 * A.1 established the principle for the runtime: **unknown is not garbage**, and
 * "there is nothing here" is a different fact from "there is something here I
 * cannot read". A.2 applies it to the other two legs.
 *
 * WHY THIS IS NOT `runtimeCompatibility.ts`. The runtime inspector carries a
 * dimension these do not - a nested encounter with its own schema version and
 * its own `newer-encounter` outcome - and it was audited in its A.1 shape.
 * Folding three inspectors into one generic record-compatibility engine would
 * buy matching filenames and cost a rewrite of approved code. What IS shared is
 * the ALGORITHM, and that lives once in `inspectLeg` below rather than being
 * typed out per leg.
 */
export type LegRefusalKind =
  /** Written by a newer build. Never migrated speculatively. */
  | 'newer-version'
  /** Same version, but this build's validator refuses it. */
  | 'invalid-record'
  /** Well-formed, but it describes a different crawler. */
  | 'identity';

export type LegInspection<T> =
  | { ok: true; record: T }
  | { ok: false; kind: LegRefusalKind; message: string };

export class SessionLoadRefusal extends Error {
  readonly kind: LegRefusalKind;

  constructor(kind: LegRefusalKind, message: string) {
    super(message);
    this.name = 'SessionLoadRefusal';
    this.kind = kind;
  }
}

interface LegSpec<T> {
  /** What the player is told this record is. */
  label: string;
  currentVersion: number;
  migrate: (raw: unknown) => unknown;
  validate: (record: unknown) => { valid: boolean; errors: string[] };
  ownerOf: (record: Record<string, unknown>) => unknown;
  cast: (record: unknown) => T;
}

/**
 * One algorithm, and the ORDER inside it is the safety argument.
 *
 * The version is read off the RAW record, before the migration chain runs,
 * because carrying a shape from the future through steps written for the past
 * is guesswork dressed as compatibility. Identity is checked before validation
 * so a record about the wrong crawler is reported as such rather than as
 * generic corruption - a caller that has to read prose to tell those apart will
 * eventually read it wrong.
 *
 * PURE. It reads and decides. It cannot write, delete or repair, which is the
 * cheapest way to guarantee that a build which did not understand the data did
 * not alter the data.
 */
function inspectLeg<T>(raw: unknown, characterId: string, spec: LegSpec<T>): LegInspection<T> {
  const unreadable = (detail?: string): LegInspection<T> => ({
    ok: false,
    kind: 'invalid-record',
    message:
      `This crawler has saved ${spec.label} state that cannot be safely read. ` +
      `Nothing has been changed.${detail === undefined ? '' : ` (${detail})`}`,
  });

  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return unreadable();

  const record = raw as Record<string, unknown>;

  const version = record['schemaVersion'];
  if (typeof version === 'number' && version > spec.currentVersion) {
    return {
      ok: false,
      kind: 'newer-version',
      message:
        `This crawler's ${spec.label} state was written by a newer version of the ` +
        `application (format ${version}; this build understands ${spec.currentVersion}). ` +
        `Open the crawler with that version. Nothing has been changed.`,
    };
  }

  const migrated = spec.migrate(record);
  if (typeof migrated !== 'object' || migrated === null) return unreadable();

  // IDENTITY MEANS "NAMES SOMEONE ELSE", not "names nobody". A record with no
  // usable id is malformed, and saying it belongs to another crawler would be a
  // more specific claim than the evidence supports. Those fall through to the
  // validator, which is the thing that knows the field is required.
  const owner = spec.ownerOf(migrated as Record<string, unknown>);
  if (typeof owner === 'string' && owner !== characterId) {
    return {
      ok: false,
      kind: 'identity',
      message:
        `The saved ${spec.label} state does not belong to this crawler ` +
        `(it names "${String(owner)}"). Nothing has been changed.`,
    };
  }

  const check = spec.validate(migrated);
  if (!check.valid) return unreadable(check.errors.join(' '));

  return { ok: true, record: spec.cast(migrated) };
}

export function inspectCharacterRecord(
  raw: unknown,
  characterId: string,
): LegInspection<Character> {
  return inspectLeg(raw, characterId, {
    label: 'character',
    currentVersion: CHARACTER_SCHEMA_VERSION,
    migrate: migrateCharacterRecord,
    validate: validateCharacter,
    ownerOf: (record) => record['id'],
    cast: (record) => record as Character,
  });
}

export function inspectSessionRecord(
  raw: unknown,
  characterId: string,
): LegInspection<SessionState> {
  return inspectLeg(raw, characterId, {
    label: 'session',
    currentVersion: SESSION_SCHEMA_VERSION,
    migrate: migrateSessionRecord,
    validate: validateSession,
    ownerOf: (record) => record['characterId'],
    cast: (record) => record as SessionState,
  });
}
